import React, { useLayoutEffect, useRef, ReactNode } from "react";

import Grid from "@material-ui/core/Grid";
import styled from "styled-components/macro";

import { SubtleBadge } from "components/Badge";
import { HideShowButton } from "components/Button";
import Collapse from "components/Collapse";
import { Row } from "components/Layout";

import { getDataTourFromChildren } from "lib/dataTour";

// Bit of a workaround for the issue discussed here
// https://github.com/mui-org/material-ui/issues/7466
export const FormGrid = styled(Grid)<{ spacing: number }>`
  && {
    padding: 0 ${({ spacing }) => `${(spacing / 2) * 8}px`};
    margin: 0;
    max-width: 100%;
  }
`;

export const FormSectionGrid = styled(FormGrid)`
  && {
    flex-grow: 0;
    flex-basis: 100%;
  }
`;

const CollapseWrapper = styled.div`
  margin: 0 -12px;
`;

export const HeaderRow = styled(Row)<{ hasErrorBadge?: boolean }>`
  margin: 0 -12px;
  padding: ${({ theme }) => `${theme.space[1]}px ${theme.space[2]}px`};
  ${({ hasErrorBadge }) => hasErrorBadge && "background-color: #FFF4EE;"};
`;

export const HeaderText = styled.span`
  margin-left: 12px;
`;

interface FormCollapseProps {
  isOpen?: boolean;
  onToggle?: () => void;
  header: ReactNode;
  children: ReactNode;
  hasErrorBadge?: boolean;
  text?: string;
  id?: string | number;
  scrollIntoView?: boolean;
  dataTour?: string;
}

export const FormCollapse: React.FC<FormCollapseProps> = ({
  isOpen = true,
  onToggle,
  header,
  children,
  hasErrorBadge = false,
  text = undefined,
  id = 0,
  scrollIntoView = true,
  dataTour,
}) => {
  const resolvedDataTour =
    dataTour || getDataTourFromChildren(header) || "formCollapse";

  const headerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (headerRef.current && isOpen && scrollIntoView) {
      // Wait 250ms for the collapse to expand then scroll to the collapse header into view.
      setTimeout(() => {
        if (headerRef.current && isOpen) {
          headerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "start",
          });
        }
      }, 250);
    }
  }, [isOpen, scrollIntoView]);

  const showCollapse = Boolean(isOpen);

  return (
    <>
      <HeaderRow
        justifyBetween
        alignCenter
        hasErrorBadge={hasErrorBadge}
        onClick={onToggle}
        data-tour={resolvedDataTour}
        id={id.toString()}
        ref={headerRef}
      >
        <HeaderText>{header}</HeaderText>
        {typeof onToggle === "function" && (
          <HideShowButton isOpen={showCollapse} text={text} />
        )}
      </HeaderRow>
      <CollapseWrapper data-tour={`${resolvedDataTour}Section`}>
        <Collapse isOpen={showCollapse}>
          <FormSectionGrid container spacing={2}>
            {children}
          </FormSectionGrid>
        </Collapse>
      </CollapseWrapper>
    </>
  );
};

export const CollapseLabel = styled.span(
  ({ theme }) => `
  font-size: ${theme.fontSizes.alpha}px;
  font-weight: ${theme.fontWeights.bold};
  margin: 0 ${theme.space[3]}px;
  align-self: center;
  display: flex;
`,
);

interface CollapseHeaderProps {
  title: string;
  children?: ReactNode;
}

export const CollapseHeader: React.FC<CollapseHeaderProps> = ({
  title,
  children,
}) => (
  <Row>
    <SubtleBadge>{title}</SubtleBadge>
    {children && <CollapseLabel>{children}</CollapseLabel>}
  </Row>
);
